import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useCookingReminder, useJournalReminder } from "@/lib/reminders";
import { LayoutDashboard, CalendarDays, Target, ShoppingCart, Settings, Leaf, LogOut, Crown, Users, BookOpen, NotebookPen } from "lucide-react";

export function AppShell({ children }) {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  useCookingReminder();
  useJournalReminder();

  const items = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("nav_dashboard") },
    { to: "/meal-plan", icon: CalendarDays, label: t("nav_plan") },
    { to: "/journal", icon: NotebookPen, label: t("nav_journal") },
    { to: "/goals", icon: Target, label: t("nav_goals") },
    { to: "/grocery", icon: ShoppingCart, label: t("nav_grocery") },
    { to: "/recipes", icon: BookOpen, label: t("nav_recipes") },
    { to: "/household", icon: Users, label: t("nav_household") },
  ];
  const mobileItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("nav_dashboard") },
    { to: "/meal-plan", icon: CalendarDays, label: t("nav_plan") },
    { to: "/grocery", icon: ShoppingCart, label: t("nav_grocery") },
    { to: "/settings", icon: Settings, label: t("nav_settings") },
  ];
  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#FAF6F0]/90 dark:bg-background/90 backdrop-blur-md border-b border-border">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" data-testid="app-logo" className="flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-2xl bg-primary text-primary-foreground">
              <Leaf className="h-5 w-5" />
            </span>
            <span className="font-serif text-2xl font-semibold tracking-tight">CHENIA</span>
          </Link>

          <nav className="hidden md:flex items-center gap-0.5 lg:gap-1">
            {items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.to.replace("/", "")}`}
                className={`px-3 lg:px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(it.to) ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-secondary"
                }`}
              >
                {it.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/pricing" data-testid="nav-pricing"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                isActive("/pricing") ? "bg-primary text-primary-foreground" : "bg-chenia-rosesoft text-chenia-terracottadark hover:bg-chenia-rose/40"}`}>
              <Crown className="h-4 w-4" /> <span className="hidden sm:inline">{user?.plan_tier && user.plan_tier !== "free" ? user.plan_tier.toUpperCase() : t("pricing")}</span>
            </Link>
            <LanguageSelector />
            <div className="hidden sm:flex items-center gap-2 pl-1">
              <Link to="/settings" data-testid="nav-settings" className="grid place-items-center h-9 w-9 rounded-full border border-border hover:bg-secondary transition-colors" title={t("nav_settings")}>
                <Settings className="h-4 w-4" />
              </Link>
              <button
                data-testid="logout-btn"
                onClick={doLogout}
                className="grid place-items-center h-9 w-9 rounded-full border border-border hover:bg-secondary transition-colors"
                title={t("logout")}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-8 py-6 lg:py-10 pb-28 md:pb-10">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#FAF6F0]/95 dark:bg-background/95 backdrop-blur-md border-t border-border">
        <div className="grid grid-cols-4">
          {mobileItems.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`mobile-nav-${it.to.replace("/", "")}`}
                className={`flex flex-col items-center gap-1 py-2.5 text-xs ${
                  isActive(it.to) ? "text-primary font-semibold" : "text-muted-foreground"
                }`}
              >
                <Icon className="h-5 w-5" />
                {it.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
